<template>
  <div class="product-faq-list">
    <div
      v-for="(item, index) in items"
      :key="index"
      class="product-faq-item"
      :class="{ 'is-open': openedIndexes.has(index) }"
    >
      <button
        type="button"
        class="product-faq-q"
        :aria-expanded="openedIndexes.has(index)"
        :aria-controls="`${idPrefix}-faq-${index}`"
        @click="toggle(index)"
      >
        <span class="product-faq-q_mark">Q</span>
        <span class="product-faq-q_text">{{ item.question }}</span>
        <span class="product-faq-arrow"></span>
      </button>
      <PartsCollapse :show="openedIndexes.has(index)">
        <div :id="`${idPrefix}-faq-${index}`" class="product-faq-a">
          <span class="product-faq-a_mark">A</span>
          <div class="product-faq-a_body">
            <p>
              <template v-for="(line, lineIndex) in item.answer" :key="lineIndex">
                {{ line }}<br v-if="lineIndex < item.answer.length - 1" />
              </template>
            </p>
          </div>
        </div>
      </PartsCollapse>
    </div>
  </div>

  <div v-if="footerLink" class="product-faq-footer">
    <NuxtLink :to="footerLink.href" class="product-faq-flag">
      <img src="/image/img_guide001.png" alt="" />
      <p>
        <span class="pc-only">{{ footerLink.pcLabel }}</span>
        <span class="sp-only">{{ footerLink.spLabel }}</span>
      </p>
    </NuxtLink>
    <NuxtLink
      v-if="footerLink.buttonLabel"
      :to="footerLink.href"
      class="btn-round product-faq-more-btn"
    >
      {{ footerLink.buttonLabel }}<span class="btn-round_arrow"></span>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-faq-list`（商品詳細のFAQ）。34/38（`QBD01` `QBD02` `QBM01` `QBM02` に無い）。
 * 中身は既存 `productContents/<商品コード>/faq.vue`（35商品・`<el-collapse-item>` 288項目）から移す。
 *
 * **開閉は独立**。支給の JS（`design-src/20260810/assets/js/script_main.js:376-386`）は
 * 押した項目だけを `slideToggle(300)` するので、**複数を同時に開ける**。
 * jQuery は持ち込まず `PartsCollapse`（300ms・決定#41）へ委譲する。
 *
 * **初期状態は全部閉じる**（決定#68）。支給は先頭の `product-faq-item` にだけ `is-open` が付くが、
 * これは静的HTMLで中身を見せるための表示であって初期状態の指定ではない。
 *
 * `is-open` は矢印の向きに要るので、`PartsCollapse` の `v-show` とは別に親 div にも付ける
 * （ガイドアコーディオンと同じ扱い）。
 * → CONVERSION-GUIDE.md §3.3.1 の #11
 */
type FaqItem = {
  question: string;
  /** 回答。1要素＝1行（決定#56） */
  answer: string[];
};

type Props = {
  items: FaqItem[];
  /** `aria-controls` 用の接頭辞。1ページに複数置くときの衝突を避ける */
  idPrefix?: string;
  /**
   * 一覧の下に出す `/faq` への導線。支給は**同じ飛び先へのリンクが2本**あり、
   * 帯（`product-faq-flag`）とボタン（`product-faq-more-btn`）が並ぶ
   * （`design-src/20260810/product-detail/product501.html:1153-1171`）。
   * `buttonLabel` を省略すると帯だけ出る。
   */
  footerLink?: {
    href: string;
    pcLabel: string;
    spLabel: string;
    buttonLabel?: string;
  } | null;
};

withDefaults(defineProps<Props>(), {
  idPrefix: 'product',
  footerLink: null,
});

/**
 * **初期状態は全部閉じる**（決定#68）。支給は先頭に `is-open` が付いているが、
 * **支給デザインの「開いた状態」は採らない** — 静的HTMLで中身を見せるための表示であって、
 * 初期状態の指定ではない。開閉するものは閉じた状態から始める。
 */
const openedIndexes = ref<Set<number>>(new Set());

const toggle = (index: number): void => {
  const next = new Set(openedIndexes.value);
  if (next.has(index)) {
    next.delete(index);
  } else {
    next.add(index);
  }
  openedIndexes.value = next;
};
</script>
